import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, ChevronUp, Settings, X } from 'lucide-react';

import { getFeatureDetails } from '../data/featureDetails';
import { services } from '../data/services';

// Remove unused interface and use type from services if needed
type ServiceType = (typeof services)[keyof typeof services][number];

const CustomPlanBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedServices, setExpandedServices] = useState<string[]>([]);

  const allServices = Object.values(services).flat() as ServiceType[];

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const toggleService = (serviceId: string) => {
    setExpandedServices(prev =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prev => {
      const newServices = prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId];
      return newServices;
    });
  };

  const handleSubmit = () => {
    const selectedServicesDetails = selectedServices
      .map(id => allServices.find(service => service.id === id))
      .filter((service): service is ServiceType => service !== undefined);

    const servicesByCategory: Record<string, ServiceType[]> = {};

    selectedServicesDetails.forEach(service => {
      if (!servicesByCategory[service.category]) {
        servicesByCategory[service.category] = [];
      }
      servicesByCategory[service.category].push(service);
    });

    // Build a detailed message with services grouped by category
    let detailedMessage =
      'Estoy interesado en un plan personalizado con los siguientes servicios:\n\n';

    Object.entries(servicesByCategory).forEach(([category, categoryServices]) => {
      detailedMessage += `CATEGORÍA: ${category.toUpperCase()}\n`;

      categoryServices.forEach(service => {
        if (service) {
          detailedMessage += `- ${service.title}\n`;
          detailedMessage += `  Descripción: ${service.description}\n`;

          if (service.features && service.features.length > 0) {
            detailedMessage += `  Características principales:\n`;
            service.features.slice(0, 3).forEach(feature => {
              detailedMessage += `    • ${feature}\n`;
            });
          }
          detailedMessage += '\n';
        }
      });
    });

    detailedMessage += 'Me gustaría recibir información detallada sobre:\n';
    detailedMessage += '- Costos y planes disponibles para esta combinación de servicios\n';
    detailedMessage += '- Tiempo estimado de implementación\n';
    detailedMessage += '- Proceso de inicio y siguientes pasos';

    // Close the modal before navigating
    setIsModalOpen(false);

    navigate('/contacto', {
      state: {
        subject: 'Solicitud de Plan Personalizado',
        message: detailedMessage,
      },
    });
  };

  const handleOpenModal = () => {
    const button = document.querySelector('.plan-button');
    if (button) {
      const buttonRect = button.getBoundingClientRect();
      const scrollY = window.scrollY;

      // Ajustado el margen a 10px (antes era 20px)
      const modalTopPosition = scrollY + buttonRect.top - 10;

      setIsModalOpen(true);

      setTimeout(() => {
        window.scrollTo({
          // Reducido el espacio superior a 50px (antes era 100px)
          top: Math.max(0, modalTopPosition - 50),
          behavior: 'smooth',
        });
      }, 100);
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all max-w-2xl mx-auto"
      >
        <div className="bg-blue-100 rounded-lg w-16 h-16 flex items-center justify-center mb-4">
          <Settings className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold mb-2">Plan Personalizado</h3>
        <p className="text-gray-600 mb-6">
          Crea un plan personalizado seleccionando los servicios específicos que necesitas
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleOpenModal}
          className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors plan-button" // Añadida clase plan-button
        >
          Seleccionar servicios
          <ArrowRight className="ml-2 h-4 w-4" />
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setIsModalOpen(false)}
              data-testid="modal-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -25 }} // Reducido y: -50 a y: -25
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -25 }}
              className="fixed left-[30%] top-[45%] -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl z-50 flex flex-col w-[90%] md:w-[800px] max-h-[80vh] overflow-hidden modal-container"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between bg-blue-50/80 backdrop-blur-sm shrink-0">
                <div>
                  <h3 className="text-2xl font-bold text-blue-900">Personaliza tu Plan</h3>
                  <p className="text-blue-600 mt-1">Selecciona los servicios que necesitas</p>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setIsModalOpen(false);
                  }}
                  className="p-2 hover:bg-blue-100 rounded-full transition-colors"
                >
                  <X className="h-6 w-6 text-blue-900" />
                </button>
              </div>

              {/* Content area with auto height */}
              <div className="flex-1 overflow-y-auto p-3 scrollbar-thin scrollbar-thumb-blue-200 scrollbar-track-transparent">
                <div className="space-y-3 flex flex-col min-h-0">
                  {/* Orden personalizado de categorías */}
                  {[
                    'digitales',
                    'operaciones',
                    'estrategicos',
                    'soporte',
                    'asesoria',
                    'comunicacion',
                    'investigacion',
                    'formacion',
                    'transformacion',
                  ]
                    .filter(category => services[category as keyof typeof services])
                    .map(category => {
                      const categoryServices = services[category as keyof typeof services];
                      return (
                        <motion.div
                          key={category}
                          className="border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                          animate={{
                            height: expandedCategories.includes(category) ? 'auto' : '52px',
                            marginBottom: expandedCategories.includes(category) ? '0.75rem' : '0',
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <div
                            className={`p-3 flex items-center justify-between cursor-pointer transition-all ${
                              expandedCategories.includes(category)
                                ? 'bg-blue-50/70 border-b'
                                : 'hover:bg-gray-50/80'
                            }`}
                            onClick={() => toggleCategory(category)}
                          >
                            <h4 className="font-semibold text-lg text-gray-800 capitalize">
                              {category} ({categoryServices.length})
                            </h4>
                            {expandedCategories.includes(category) ? (
                              <ChevronUp className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500" />
                            )}
                          </div>

                          <AnimatePresence>
                            {expandedCategories.includes(category) && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="divide-y divide-gray-100"
                              >
                                {categoryServices.map(service => (
                                  <motion.div
                                    key={service.id}
                                    className="p-3 hover:bg-gray-50/60 transition-colors"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    data-testid={`service-${service.id}`}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div
                                        className={`w-6 h-6 rounded-lg border-2 flex-shrink-0 cursor-pointer flex items-center justify-center transition-all ${
                                          selectedServices.includes(service.id)
                                            ? 'bg-blue-600 border-blue-600 shadow-sm'
                                            : 'border-gray-300 hover:border-blue-400 hover:shadow-sm'
                                        }`}
                                        onClick={e => {
                                          e.stopPropagation(); // Prevent event bubbling
                                          handleServiceToggle(service.id);
                                        }}
                                      >
                                        {selectedServices.includes(service.id) && (
                                          <Check className="h-4 w-4 text-white" />
                                        )}
                                      </div>
                                      <div className="flex-grow">
                                        <div
                                          className="flex items-center space-x-2 cursor-pointer"
                                          onClick={e => {
                                            e.stopPropagation();
                                            toggleService(service.id);
                                          }}
                                        >
                                          <h5 className="text-lg font-medium text-gray-800">
                                            {service.title}
                                          </h5>
                                          {expandedServices.includes(service.id) ? (
                                            <ChevronUp className="h-4 w-4 text-gray-500" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">
                                          {service.description}
                                        </p>

                                        <AnimatePresence>
                                          {expandedServices.includes(service.id) && (
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: 'auto', opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              className="mt-3 space-y-2"
                                            >
                                              {service.features.map((feature, index) => {
                                                const details = getFeatureDetails(feature);
                                                return (
                                                  <div
                                                    key={index}
                                                    className="bg-gray-50/80 p-3 rounded-xl text-sm border border-gray-100"
                                                  >
                                                    <div className="font-medium text-gray-700 mb-1">
                                                      {feature}
                                                    </div>
                                                    <p className="text-gray-600 text-xs">
                                                      {details.description}
                                                    </p>
                                                  </div>
                                                );
                                              })}
                                            </motion.div>
                                          )}
                                        </AnimatePresence>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                </div>
              </div>

              {/* Footer - Now with automatic positioning */}
              <div className="mt-auto border-t bg-gray-50/80 backdrop-blur-sm shadow-lg shrink-0">
                <div className="p-3 flex items-center justify-between">
                  <p className="text-sm text-gray-600 font-medium">
                    Servicios seleccionados:{' '}
                    <span className="text-blue-600">{selectedServices.length}</span>
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={selectedServices.length === 0}
                    className={`flex items-center justify-center px-5 py-2 rounded-xl transition-all text-base font-medium ${
                      selectedServices.length === 0
                        ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    Solicitar información
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomPlanBuilder;
